import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/AuthContext";
import { toast } from "sonner";
import { drawLottery, DrawResult, DrawResultItem } from "@/services/customerService";

type GachaType = 'single' | 'multi10' | 'multi100';

export const useGacha = () => {
  const { currentUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false);
  const [isPointDialogOpen, setIsPointDialogOpen] = useState(false);
  const [isPhoneVerificationRequired, setIsPhoneVerificationRequired] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(false);
  const [pendingGacha, setPendingGacha] = useState<{
    siteId: string;
    eventId: string;
    cost: number;
    count: number;
    shippingFee: number;
  } | null>(null);
  // 抽選結果とアニメーション再生用の状態
  const [drawResults, setDrawResults] = useState<DrawResultItem[]>([]);
  const [currentAnimationIndex, setCurrentAnimationIndex] = useState(0);
  const [currentAnimationUrl, setCurrentAnimationUrl] = useState<string | null>(null);
  const [lotteryResultIds, setLotteryResultIds] = useState<number[]>([]);

  const handleGachaClick = (
    e: React.MouseEvent,
    gachaType: GachaType,
    siteId: string,
    eventId: string,
    gachaCost: number,
    shippingFee: number = 600
  ) => {
    e.preventDefault();

    // 未ログイン処理
    if (!currentUser) {
      setIsLoginDialogOpen(true);
      return;
    }

    // ポイント計算（くじ代 + 配送料）
    const cost = calculateCost(gachaType, gachaCost);
    const count = getGachaCount(gachaType);
    const totalRequired = cost + shippingFee;

    // ポイント不足処理（くじ代 + 配送料）
    if ((currentUser.balance || 0) < totalRequired) {
      setIsPointDialogOpen(true);
      return;
    }

    // 確認ダイアログ表示
    setPendingGacha({ siteId, eventId, cost, count, shippingFee });
    setIsConfirmDialogOpen(true);
  };

  const executeGacha = async () => {
    if (!pendingGacha) return;

    setIsConfirmDialogOpen(false);
    setIsDrawing(true);

    try {
      // 先に抽選API呼び出し
      const result = await drawLottery(
        Number(pendingGacha.siteId),
        pendingGacha.eventId,
        pendingGacha.count
      );

      if (result.success && result.results && result.results.length > 0) {
        // 結果を保存
        setDrawResults(result.results);
        setLotteryResultIds(result.lottery_result_ids || []);

        // ポイント残高を更新
        refreshUser();

        // アニメーションの有無をチェック
        const hasAnyAnimation = result.results.some((r: DrawResultItem) => r.animation_url);

        if (hasAnyAnimation) {
          // アニメーションがある場合: アニメーション再生フロー
          setCurrentAnimationIndex(0);
          const firstAnimationUrl = result.results[0]?.animation_url;
          setCurrentAnimationUrl(firstAnimationUrl || null);
          setIsAnimationPlaying(true);
        } else {
          // 全てアニメーションなし: 直接結果ページへ遷移
          setIsDrawing(false);
          setPendingGacha(null);
          const ids = result.lottery_result_ids || [];
          if (ids.length > 0) {
            navigate(`/results?ids=${ids.join(',')}`);
          }
          setDrawResults([]);
          setLotteryResultIds([]);
          return;
        }
      } else if (result.success) {
        // 結果はあるがresultsがない場合（後方互換性）
        // ポイント残高を更新
        refreshUser();

        if (result.lottery_result_ids && result.lottery_result_ids.length > 0) {
          const idsParam = result.lottery_result_ids.join(',');
          navigate(`/results?ids=${idsParam}`);
        } else {
          toast.success(`${pendingGacha.count}回の抽選が完了しました！`);
        }
        setIsDrawing(false);
        setPendingGacha(null);
      } else {
        toast.error(result.message || "ガチャを引くことができませんでした");
        setIsDrawing(false);
        setPendingGacha(null);
      }
    } catch (err: any) {
      if (err.response?.data?.requires_phone_verification) {
        setIsPhoneVerificationRequired(true);
      } else {
        toast.error(err.response?.data?.error || err.message || "ガチャ実行中にエラーが発生しました");
      }
      setIsDrawing(false);
      setPendingGacha(null);
    }
  };

  const onAnimationEnd = async () => {
    // 次の結果へ
    const nextIndex = currentAnimationIndex + 1;

    if (nextIndex < drawResults.length) {
      // まだ結果がある場合、次のアニメーションを再生
      setCurrentAnimationIndex(nextIndex);
      const nextAnimationUrl = drawResults[nextIndex]?.animation_url;
      setCurrentAnimationUrl(nextAnimationUrl || null);
      // アニメーションがない場合でも isAnimationPlaying は true のまま
      // （AnimationlessGacha で短い遅延後に onAnimationEnd が呼ばれる）
    } else {
      // 全ての結果を表示完了
      goToResults();
    }
  };

  const skipAllAnimations = () => {
    // 全てのアニメーションをスキップして結果へ
    goToResults();
  };

  const goToResults = () => {
    setIsAnimationPlaying(false);
    setIsDrawing(false);
    setPendingGacha(null);

    // 結果ページにリダイレクト
    if (lotteryResultIds.length > 0) {
      const idsParam = lotteryResultIds.join(',');
      navigate(`/results?ids=${idsParam}`);
    }

    // 状態をリセット
    setDrawResults([]);
    setCurrentAnimationIndex(0);
    setCurrentAnimationUrl(null);
    setLotteryResultIds([]);
  };

  // ヘルパー関数
  const calculateCost = (type: GachaType, baseCost: number) => {
    return type === 'single' ? baseCost :
           type === 'multi10' ? baseCost * 10 :
           baseCost * 100;
  };

  const getGachaCount = (type: GachaType) => {
    return type === 'single' ? 1 :
           type === 'multi10' ? 10 :
           100;
  };

  return {
    handleGachaClick,
    executeGacha,
    onAnimationEnd,
    skipAllAnimations,
    isConfirmDialogOpen,
    isLoginDialogOpen,
    isPointDialogOpen,
    isDrawing,
    isAnimationPlaying,
    pendingGacha,
    setIsConfirmDialogOpen,
    setIsLoginDialogOpen,
    setIsPointDialogOpen,
    isPhoneVerificationRequired,
    setIsPhoneVerificationRequired,
    // 賞ごとアニメーション用
    currentAnimationUrl,
    currentAnimationIndex,
    drawResults,
  };
};
